import { useParams } from "react-router-dom"
import { Box, CircularProgress, Container, Typography } from "@mui/material"
import { makeStyles } from "tss-react/mui"

import { LocalizedStrict } from "../../../../localization/components/AppLocalized"
import { useDateTime } from "../../../../localization/useDateTime"
import { PaymentErrorType, PaymentStatus } from "../../settings/wallets/status/types/paymentTypes"
import { usePaymentFlow } from "../../settings/wallets/status/hooks/paymentHooks"
import { useEffectOnce } from "../../../../../shared/hooks/useEffectOnce"

import { TakeawayOrderConfirmed } from "../components/TakeawayOrderConfirmed"
import { TakeawayOrderFailed } from "../components/TakeawayOrderFailed"

const useStyles = makeStyles()((theme) => ({
    container: {
        height: "100%",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        padding: theme.spacing(3),
    },
    content: {
        padding: theme.spacing(6),
        maxWidth: "25rem",
        width: "100%",
        textAlign: "center",
    },
    title: {
        fontWeight: 600,
        marginBottom: theme.spacing(2),
        color: theme.palette.text.primary,
    },
    description: {
        color: theme.palette.text.secondary,
        lineHeight: 1.6,
        marginBottom: theme.spacing(4),
    },
    loaderContainer: {
        position: "relative",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        marginBottom: theme.spacing(2),
    },
    countDown: {
        position: "absolute",
        top: "50%",
        left: "50%",
        transform: "translate(-50%, -50%)",
    },
}))

export function TakeawayPaymentPoll() {
    const { classes } = useStyles()
    const { orderUid } = useParams<{ orderUid: string }>()
    const dateTimeFactory = useDateTime()

    const { progressState, paymentState, pollForPaymentState } = usePaymentFlow()

    useEffectOnce(() => {
        if (orderUid) {
            pollForPaymentState(orderUid)
        }
    })

    if (!orderUid) {
        return (
            <LocalizedStrict id="takeaway-payment-failed" attrs={{ title: true, description: true }}>
                <TakeawayOrderFailed
                    title="Payment Failed"
                    description="Payment could not be completed due to an unknown error. Please try again later."
                />
            </LocalizedStrict>
        )
    }

    if (paymentState.status === PaymentStatus.COMPLETED) {
        return (
            <TakeawayOrderConfirmed orders={paymentState.response.order ? [paymentState.response.order] : undefined} />
        )
    }

    if (paymentState.status === PaymentStatus.FAILED) {
        const isTimeout = paymentState.errorType === PaymentErrorType.TIMEOUT

        return isTimeout ? (
            <LocalizedStrict id="takeaway-payment-timeout" attrs={{ title: true, description: true }}>
                <TakeawayOrderFailed
                    title="Payment Timed Out"
                    description="We haven't received confirmation from your payment provider. Please check your netbank to be sure that the payment was completed."
                />
            </LocalizedStrict>
        ) : (
            <LocalizedStrict id="takeaway-payment-failed" attrs={{ title: true, description: true }}>
                <TakeawayOrderFailed
                    title="Payment Failed"
                    description="Payment could not be completed due to an unknown error. Please try again later."
                />
            </LocalizedStrict>
        )
    }

    return (
        <Container className={classes.container}>
            <Box className={classes.content}>
                <LocalizedStrict id="takeaway-payment-poll-title">
                    <Typography variant="h5" className={classes.title}>
                        Processing Payment
                    </Typography>
                </LocalizedStrict>

                {progressState?.remaining && (
                    <Box className={classes.loaderContainer}>
                        <CircularProgress size={100} thickness={4} />
                        <Box className={classes.countDown}>
                            <Typography>{dateTimeFactory.formatDuration(progressState.remaining)}</Typography>
                        </Box>
                    </Box>
                )}

                <LocalizedStrict id="takeaway-payment-poll-description">
                    <Typography variant="body1" className={classes.description}>
                        Please wait while we process your payment. This may take a few moments.
                    </Typography>
                </LocalizedStrict>
            </Box>
        </Container>
    )
}
